import { useState, useEffect, useMemo } from 'react';
import { ListEmptyState } from '@overlayqa/shared/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Folder01Icon, Search01Icon, Sorting01Icon, UserGroupIcon } from '@hugeicons/core-free-icons';
import { useProjects } from '../hooks/useProjects';
import { useAppSession } from '../hooks/useAppSession';
import { useMinimumLoading } from '../hooks/useMinimumLoading';
import { useProjectsViewMode } from '../hooks/useProjectsViewMode';
import { DeletionConfirmDialog } from '@/components/shared/DeletionConfirmDialog';
import { ToolbarSelect } from '../components/shared/ToolbarSelect';
import { DataTable } from '../components/shared/DataTable';
import { useBulkArchiveProjects, useBulkUnarchiveProjects } from '../hooks/useBulkActions';
import { useProjectDeletion } from '../hooks/useProjectDeletion';
import { ProjectsCreateModal } from './projects/ProjectsCreateModal';
import { ProjectCard } from './projects/ProjectCard';
import { ProjectCardSkeleton } from './projects/ProjectCardSkeleton';
import { ProjectsTableSkeleton } from './projects/ProjectsTableSkeleton';
import { ProjectSettingsModal } from '../components/projects/settings/ProjectSettingsModal';
import { ProjectsViewToggle } from './projects/ProjectsViewToggle';
import {
  projectColumns,
  buildProjectRowActions,
  buildProjectBulkActions,
  buildArchivedProjectRowActions,
  buildArchivedProjectBulkActions,
  projectPath,
  type Project as ProjectRow,
} from './projects/projectsPageColumns';
import { ClientReviewSection } from '../components/projects/ClientReviewSection';
import { ProjectsVisibilityBanner } from '../components/projects/ProjectsVisibilityBanner';
import { useExtensionDetection } from '../hooks/useExtensionDetection';
import { useCaptureIssues } from '../hooks/useCaptureIssues';
import { resolveCaptureIssuesUrl } from '../lib/captureIssuesUrl';
import { canWriteProject } from '../lib/projectPermissions';
import { LoadError } from '../components/shared/IssuesLoadError';

export function ProjectsPage() {
  const { data: appSession, isLoading: sessionLoading, error: sessionError, refetch: refetchSession } = useAppSession();
  const activeTeamId = appSession?.activeTeamId;

  // The "Archived" toggle is the only reachable path to unarchiving a
  // project — the default list (includeArchived unset) always excludes
  // archived rows server-side, so this is the one place they can be found
  // again after Archive. See the 2026-07-21 things_to_fix row: "no unarchive
  // route at all, and an archived selection is unfindable in the picker."
  const [showArchived, setShowArchived] = useState(false);
  const { data: projects, isLoading, error, refetch: refetchProjects } = useProjects(activeTeamId, { includePreviews: true, includeCounts: true, includeArchived: showArchived });
  // Treat session + projects as a single combined load. Floor it to 300ms so
  // fast cache hits don't strobe.
  const rawLoading = sessionLoading || (isLoading && !projects);
  const showSkeleton = useMinimumLoading(rawLoading, 300);
  const deletion = useProjectDeletion(activeTeamId);
  const bulkArchive = useBulkArchiveProjects();
  const bulkUnarchive = useBulkUnarchiveProjects();
  const { viewMode, setView } = useProjectsViewMode();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  useEffect(() => {
    if (searchParams.get('new') === '1' && activeTeamId) {
      setShowCreateModal(true);
      // Delete only the `new` param so other state (e.g. ?view=table) survives.
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.delete('new');
          return updated;
        },
        { replace: true },
      );
    }
  }, [searchParams, activeTeamId, setSearchParams]);
  const { selection: confirmDelete, open: setConfirmDelete } = deletion;
  const [settingsProjectId, setSettingsProjectId] = useState<string | null>(null);
  const [clientReviewProjectId, setClientReviewProjectId] = useState<string | null>(null);
  // Capture Issues from a project menu: opens the team review at the URL the
  // project already holds (scan URL, else primary domain; no issue list here),
  // with no modal. "Capture a different page" is the URL prompt.
  const { detected: extensionDetected } = useExtensionDetection();
  const captureIssues = useCaptureIssues({ extensionDetected,
    canCaptureProject: id => canWriteProject(projects?.find(p => p.id === id)?.role),
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated');
  const [roleFilter, setRoleFilter] = useState('');

  // The scope the current view is actually browsing: normal fetches already
  // exclude archived rows server-side, but the archived toggle re-fetches
  // WITH includeArchived: true, which mixes active + archived in one
  // response — split back apart client-side so the two views never bleed
  // into each other.
  const startCapture = (id: string) => {
    const project = projects?.find((p) => p.id === id);
    if (!canWriteProject(project?.role)) return;
    captureIssues.start({ projectId: id, resolved: resolveCaptureIssuesUrl({ project }), surface: 'projects_menu' });
  };
  const openCaptureModal = (id: string) => {
    const project = projects?.find((p) => p.id === id);
    if (!canWriteProject(project?.role)) return;
    captureIssues.openModal({ projectId: id, initialUrl: resolveCaptureIssuesUrl({ project }).url ?? undefined, surface: 'projects_menu' });
  };

  const scopedProjects = useMemo(() => {
    if (!projects) return [];
    return showArchived
      ? projects.filter((p) => !!p.archivedAt)
      : projects.filter((p) => !p.archivedAt);
  }, [projects, showArchived]);

  const filteredProjects = useMemo(() => {
    let result = [...scopedProjects];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(q) || p.primaryDomain?.toLowerCase().includes(q));
    }
    if (roleFilter) {
      result = result.filter((p) => (p.role ?? 'owner') === roleFilter);
    }
    result.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'created') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
    return result;
  }, [scopedProjects, searchQuery, sortBy, roleFilter]);

  if (sessionError || (error && !projects)) {
    return (
      <section className="page-content">
        <div className="page-content__inner">
          <LoadError
            resource="projects"
            error={(sessionError ?? error) as Error}
            onRetry={() => {
              // Refetch whichever failed; a healed session re-enables the
              // projects query on its own once activeTeamId lands.
              return Promise.all([sessionError ? refetchSession() : null, error ? refetchProjects() : null])
                .then(results => ({ isError: results.some(result => result?.isError) }));
            }}
          />
        </div>
      </section>
    );
  }

  const hasProjects = scopedProjects.length > 0;
  const isEmpty = !showSkeleton && !!projects && scopedProjects.length === 0;
  // Gates the header row (search/sort/filters/toggles), independent of the
  // current archived scope: a team with active projects but zero archived
  // ones must still see the "Back to projects" toggle while browsing an
  // empty archived view, or there would be no way back except a reload.
  const hasAnyProjects = !!projects && projects.length > 0;

  return (
    <section id="projects" className="page-content">
      {!activeTeamId && !showSkeleton && (
        <div className="mb-6 alert-warning">
          No team found. Please create or join a team first.
        </div>
      )}

      {/* Header — search, filters, new project */}
      {(showSkeleton || hasAnyProjects) && (
        <div className="list-toolbar">
          <div className="list-toolbar__filters" role="search" aria-label="Filter projects">
            <div className="relative">
              <label htmlFor="search-projects" className="sr-only">Search projects</label>
              <HugeiconsIcon icon={Search01Icon} size={14} strokeWidth={2} color="currentColor" className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" aria-hidden="true" />
              <input
                id="search-projects"
                type="text"
                className="input input-toolbar w-48 pl-8"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <ToolbarSelect
              icon={Sorting01Icon}
              value={sortBy}
              onChange={setSortBy}
              defaultValue="updated"
              aria-label="Sort by"
              options={[
                { value: 'updated', label: 'Last updated' },
                { value: 'created', label: 'Newest' },
                { value: 'name', label: 'Name' },
              ]}
            />
            <ToolbarSelect
              icon={UserGroupIcon}
              value={roleFilter}
              onChange={setRoleFilter}
              name="Role"
              aria-label="Filter by role"
              options={[
                { value: '', label: 'All roles' },
                { value: 'owner', label: 'Owner' },
                { value: 'collaborator', label: 'Shared' },
                { value: 'viewer', label: 'View only' },
                { value: 'team', label: 'Team' },
              ]}
            />
            {roleFilter && (
              <button
                type="button"
                className="btn-ghost btn-toolbar px-2 text-gray-700"
                onClick={() => setRoleFilter('')}
              >
                <span className="text-cap-trim">Clear filters</span>
              </button>
            )}
          </div>
          <div className="list-toolbar__actions">
            <button
              type="button"
              className="btn-ghost btn-toolbar px-2 text-gray-700 whitespace-nowrap"
              onClick={() => setShowArchived((v) => !v)}
              aria-pressed={showArchived}
            >
              <span className="text-cap-trim">{showArchived ? 'Back to projects' : 'View archived'}</span>
            </button>
            <div className="flex shrink-0 items-center gap-3">
              <ProjectsViewToggle value={viewMode} onChange={setView} />
              <button
                className="btn-primary btn-toolbar gap-2 whitespace-nowrap"
                onClick={() => setShowCreateModal(true)}
                disabled={!activeTeamId}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" aria-hidden="true"><path d="M12 4v16m8-8H4" /></svg>
                <span className="text-cap-trim">New project</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Owner-only prompt: private, unshared projects a teammate cannot open
          (P0 2026-10-15). Active view only; the archived list is not theirs
          to open up. Renders nothing for a solo team or when nothing is hidden. */}
      {!showSkeleton && !showArchived && activeTeamId && (
        <ProjectsVisibilityBanner teamId={activeTeamId} projects={projects} />
      )}

      {/* Skeleton — shape matches the active viewMode so hydration doesn't shift layout */}
      {showSkeleton && (
        viewMode === 'grid' ? (
          <div className="skeleton-fade-in grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" aria-busy="true" aria-live="polite">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <ProjectCardSkeleton key={i} index={i} />
            ))}
          </div>
        ) : (
          <ProjectsTableSkeleton />
        )
      )}

      {/* No results: projects exist but the search/filter matches none.
          Without this, a search miss rendered a BLANK grid + "0 projects"
          with no guidance (found by the global-search app journey). */}
      {!showSkeleton && hasProjects && filteredProjects.length === 0 && (
        <ListEmptyState title="No matching projects"
          hint={searchQuery.trim() ? roleFilter ? 'No projects match your search and filters.' : 'No projects match your search.' : 'No projects match the selected filters.'}
          action={<button type="button" className="oq-btn oq-btn--secondary"
            onClick={() => { setSearchQuery(''); setRoleFilter(''); }}
          >
            <span className="text-cap-trim">{searchQuery.trim() ? roleFilter ? 'Clear search and filters' : 'Clear search' : 'Clear filters'}</span>
          </button>} />
      )}

      {/* Real Grid or Table */}
      {!showSkeleton && hasProjects && filteredProjects.length > 0 && (
        <div className="data-fade-in">
          {viewMode === 'grid' ? (
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5${deletion.busy || bulkArchive.isPending || bulkUnarchive.isPending ? ' opacity-60 pointer-events-none' : ''}`}>
              {filteredProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  archived={showArchived}
                  onOpenSettings={(id) => setSettingsProjectId(id)}
                  onArchive={(id) => { bulkArchive.mutateAsync({ ids: [id] }).catch(() => {}); }}
                  onUnarchive={(id) => { bulkUnarchive.mutateAsync({ ids: [id] }).catch(() => {}); }}
                  onDelete={(id) => setConfirmDelete({ ids: [id] })}
                  onLiveReview={(id) => startCapture(id)}
                  onLiveReviewOther={(id) => openCaptureModal(id)}
                  onClientReview={(id) => setClientReviewProjectId(id)}
                />
              ))}
            </div>
          ) : (
            <DataTable<ProjectRow>
              columns={projectColumns}
              data={filteredProjects}
              rowKey={(p) => p.id}
              rowActions={
                showArchived
                  ? buildArchivedProjectRowActions({ bulkUnarchive, setConfirmDelete })
                  : buildProjectRowActions({
                      onOpenSettings: (id) => setSettingsProjectId(id),
                      onLiveReview: (id) => startCapture(id),
                      onLiveReviewOther: (id) => openCaptureModal(id),
                      onClientReview: (id) => setClientReviewProjectId(id),
                      bulkArchive,
                      setConfirmDelete,
                    })
              }
              bulkActions={
                showArchived
                  ? buildArchivedProjectBulkActions({ bulkUnarchive, setConfirmDelete })
                  : buildProjectBulkActions({ bulkArchive, setConfirmDelete })
              }
              bulkActionPlacement="bottom"
              emptyState={null}
              rowLabel={(p) => p.name}
              /* Same destination as the row's own name link (projectPath). */
              onRowClick={(p) => navigate(projectPath(p))}
              loading={deletion.busy || bulkArchive.isPending || bulkUnarchive.isPending}
            />
          )}

          {/* Footer count */}
          <p className="text-xs text-gray-400 mt-3">
            {filteredProjects.length} {showArchived ? 'archived ' : ''}project{filteredProjects.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Empty State */}
      {isEmpty && showArchived && (
        <div className="data-fade-in text-center py-12">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <HugeiconsIcon icon={Folder01Icon} size={20} strokeWidth={2} color="currentColor" className="w-5 h-5 text-gray-400" aria-hidden="true" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">No archived projects</h3>
          <p className="text-sm text-gray-400 mb-4">Projects you archive show up here, with a way back.</p>
          <button
            type="button"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            onClick={() => setShowArchived(false)}
          >
            <span className="text-cap-trim block">Back to projects</span>
          </button>
        </div>
      )}
      {isEmpty && !showArchived && (
        <div className="data-fade-in text-center py-12">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <HugeiconsIcon icon={Folder01Icon} size={20} strokeWidth={2} color="currentColor" className="w-5 h-5 text-gray-400" aria-hidden="true" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">No projects yet</h3>
          <p className="text-sm text-gray-400 mb-4">Create your first project to start tracking design QA</p>
          <button
            className="btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <span className="text-cap-trim">+ Create project</span>
          </button>
          {/* Reachable even when nothing else on the page renders the
              header toggle (a team whose ONLY projects are archived has
              zero active rows, so hasAnyProjects is false too). */}
          <div>
            <button
              type="button"
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors mt-3"
              onClick={() => setShowArchived(true)}
            >
              <span className="text-cap-trim block">View archived projects</span>
            </button>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <DeletionConfirmDialog
        open={confirmDelete !== null}
        request={confirmDelete ? { kind: 'projects', ids: confirmDelete.ids } : null}
        fallback={{
          title: 'Delete projects',
          message: `Delete ${confirmDelete?.ids.length ?? 0} project(s)? All issues in these projects will also be deleted. This cannot be undone.`,
          confirmLabel: 'Delete',
        }}
        onConfirm={() => void deletion.confirm()}
        onCancel={() => setConfirmDelete(null)}
        loading={deletion.busy}
        error={deletion.error}
        confirmLabelOverride={deletion.needsCheck ? 'Check project status' : undefined}
      />

      {/* Create Project Modal */}
      {showCreateModal && activeTeamId && (
        <ProjectsCreateModal
          activeTeamId={activeTeamId}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Project Settings: the card and row Settings item. The modal loads the
          project itself, so it shows fresh figmaFiles and role. */}
      {settingsProjectId && (
        <ProjectSettingsModal
          projectId={settingsProjectId}
          onClose={() => setSettingsProjectId(null)}
        />
      )}

      {/* Team Review: the modal only when the project holds no reachable URL,
          the create failed, or "Capture a different page" asked for it. */}
      {captureIssues.modal}

      {/* Client Review Management */}
      {clientReviewProjectId && canWriteProject(projects?.find(p => p.id === clientReviewProjectId)?.role) && (
        <ClientReviewSection
          projectId={clientReviewProjectId}
          onClose={() => setClientReviewProjectId(null)}
        />
      )}
    </section>
  );
}
